import { i18n } from 'boot/i18n';
import { formatCalendarDate } from 'src/utils/DateUtils';

/**
 * Demographics into the rows a person picker renders.
 *
 * A PRESENTER, so the picker stays a general component. It takes rows with a name, a supporting line
 * and an optional caveat, and knows nothing about payers, dates of birth or addresses. Deciding what
 * identifies a person is this context's judgement, not the picker's, and this is where that judgement
 * lives.
 *
 * WHAT IDENTIFIES SOMEBODY, in order of what a coordinator can act on. A date of birth first, because
 * it is what tells two people with the same name apart and it is what the payer matches on. An
 * address when there is no date of birth, because it is the next thing that distinguishes anyone. And
 * an explicit caveat when the date of birth is missing, rather than a blank, because a blank reads as
 * "not shown here" while the caveat reads as "not on file", which is the fact that matters: readiness
 * will hold on it.
 *
 * The date is shown as a date, not as an age. An age is derived, and a derived value on a row whose
 * job is telling two records apart is one more thing that can be right about the wrong person.
 */

/** The parts of an address worth showing on one line, in the order somebody reads them. */
function addressLine({ addressLine1, city, state, postalCode }) {
  const locality = [city, state].filter(Boolean).join(', ');

  return [addressLine1, locality, postalCode].filter(Boolean).join(' · ') || null;
} // addressLine()

function initialsOf(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
} // initialsOf()

/**
 * @param {import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics} person
 * @param {{ subjectId?: string|null }} [context] - `subjectId` marks the one candidate who is the
 *   client themselves
 * @returns {Object} a row the picker can render
 */
export function toPersonPickerRow(person, { subjectId = null } = {}) {
  const name = person.fullName
    ?? [person.firstName, person.lastName].filter(Boolean).join(' ')
    // Nothing at all is possible: the only floor on creating a person is that one name part exists,
    // and a record predating that floor may have neither.
    ?? '';

  // Nullish rather than null: an absent key is as missing as an empty one, and reading it as
  // present formats undefined into the caption and drops the caveat this presenter exists for.
  const hasDateOfBirth = (person.dateOfBirth ?? null) !== null;

  return {
    id: person.personId,
    name: name || i18n.t('personPicker.unnamed'),
    initials: initialsOf(name),
    // Falls back to the stored string, as the date field does: a date we cannot format is still more
    // use to somebody comparing two records than a blank line.
    caption: hasDateOfBirth
      ? (formatCalendarDate(person.dateOfBirth) ?? person.dateOfBirth)
      : addressLine(person),
    warning: hasDateOfBirth ? null : i18n.t('personPicker.noDateOfBirth'),
    badge: person.personId === subjectId ? i18n.t('personPicker.badgeClient') : null,
  };
} // toPersonPickerRow()

/**
 * @param {import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics[]} people
 * @param {{ subjectId?: string|null }} [context]
 * @returns {Object[]}
 */
export function toPersonPickerRows(people, context) {
  return people.map((person) => toPersonPickerRow(person, context));
} // toPersonPickerRows()
